import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Result, Spin } from 'antd';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { API, TOKEN_LS_PATH } from '../api';
import { PageTitle } from '../components/common/page-title';
import { CourseCarousel } from '../components/play-course/course-carousel';
import {
  TPlayerIndexes,
  VideoPlayerIndexesBar,
  normalizeIndexValue,
} from '../components/video-player-indexes-bar';
import { PLAYLIST_ID_PARAM_NAME } from '../hooks/use-playlist-path-id';
import { Paths } from '../types/common';
import { TPlayListItem, TPlaylistContentItem } from '../types/responses/video';
import { TCourseTreeNode, buildCourseTree } from '../utils/course-structure';
import {
  getPlayCourseFilePath,
  getPlayCourseItemPath,
  getPlayCourseLessonPath,
  getPlayCoursePath,
  getPlayCourseSubtopicFilePath,
  getPlayCourseSubtopicPath,
  getPlayerLink,
} from '../utils/paths';
import { isVideoPlaylistItem } from '../utils/playlist-item-kind';
import { getAiManagerModule1DisplayTitle } from './aimanager-module1-display-titles';

const isDebugVideoPlayerEnabled = () =>
  typeof window !== 'undefined' &&
  window.localStorage.debugVideoPlayer === 'true';

const PageFrame = styled('div')`
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;

  @media (max-width: 720px), (pointer: coarse) {
    width: calc(100% + 24px);
    max-width: none;
    margin: -12px -12px 0;
  }

  @media (min-width: 721px) and (max-width: 948px),
    (min-width: 721px) and (pointer: coarse) {
    width: calc(100% + 40px);
    margin: -20px -20px 0;
  }
`;

const Toolbar = styled('div')`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  padding: 8px 0;
  background: ${({ theme }) => theme.COLORS.BLACK._100};

  @media (max-width: 720px), (pointer: coarse) {
    top: env(safe-area-inset-top, 0px);
    gap: 8px;
    margin-bottom: 10px;
    padding: 8px 12px 10px;

    .ant-btn {
      min-width: 44px;
      min-height: 44px;
      padding-inline: 8px;
    }
  }

  @media (pointer: coarse) and (orientation: landscape) {
    position: static;
  }
`;

const CourseButton = styled(Button)`
  border-color: ${({ theme }) => theme.COLORS.ACCENT._200}cc;
  color: ${({ theme }) => theme.COLORS.ACCENT._200};
  background: ${({ theme }) => theme.COLORS.BLACK._400};
  box-shadow: 0 0 18px ${({ theme }) => theme.COLORS.ACCENT._200}22;

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.COLORS.ACCENT._100} !important;
    color: ${({ theme }) => theme.COLORS.ACCENT._100} !important;
    background: ${({ theme }) => theme.COLORS.ACCENT._800}22 !important;
  }

  @media (max-width: 720px), (pointer: coarse) {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    width: 78px;
    height: 56px;
    line-height: 1;
    white-space: normal;

    .anticon {
      margin: 0 !important;
      font-size: 18px;
    }
  }
`;

const Viewer = styled('iframe', {
  shouldForwardProp: (prop) => prop !== '$fileViewer',
})<{ $fileViewer?: boolean }>`
  display: block;
  width: 100%;
  box-sizing: border-box;
  height: ${({ $fileViewer }) =>
    $fileViewer ? 'calc(100vh - 154px)' : 'calc(100vh - 190px)'};
  min-height: 560px;
  border: 1px solid ${({ theme }) => theme.COLORS.BLACK._500};
  border-radius: 8px;
  background: ${({ theme }) => theme.COLORS.BLACK._400};

  @media (max-width: 720px), (pointer: coarse) {
    height: calc(100svh - 96px - env(safe-area-inset-top, 0px));
    min-height: 420px;
    border-radius: 0;
    border-right: 0;
    border-left: 0;
  }

  @media (pointer: coarse) and (orientation: landscape) {
    height: 100svh;
    min-height: 0;
    border: 0;
  }

  @supports not (height: 100svh) {
    @media (max-width: 720px), (pointer: coarse) {
      height: calc(100vh - 96px - env(safe-area-inset-top, 0px));
    }

    @media (pointer: coarse) and (orientation: landscape) {
      height: 100vh;
    }
  }
`;

const ViewerFrame = styled('div', {
  shouldForwardProp: (prop) => prop !== '$qualified',
})<{ $qualified: boolean }>`
  position: relative;
  border-radius: 8px;
  box-shadow: ${({ $qualified, theme }) =>
    $qualified
      ? `0 0 0 2px ${theme.COLORS.ACCENT._200}, 0 0 24px ${theme.COLORS.ACCENT._200}33`
      : 'none'};

  @media (max-width: 720px), (pointer: coarse) {
    border-radius: 0;
    box-shadow: ${({ $qualified, theme }) =>
      $qualified
        ? `inset 0 0 0 2px ${theme.COLORS.ACCENT._200}, 0 0 20px ${theme.COLORS.ACCENT._200}26`
        : 'none'};
  }
`;

const QualifiedBadge = styled('div')`
  position: absolute;
  top: 9px;
  left: 10px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 6px;
  border: 1px solid ${({ theme }) => theme.COLORS.ACCENT._200}99;
  border-radius: 7px;
  color: ${({ theme }) => theme.COLORS.ACCENT._100};
  background: ${({ theme }) => theme.COLORS.ACCENT._800}24;
  box-shadow: 0 0 14px ${({ theme }) => theme.COLORS.ACCENT._200}20;
  font-size: 9px;
  font-weight: 400;
  line-height: 1;
  pointer-events: none;

  .anticon {
    color: ${({ theme }) => theme.COLORS.ACCENT._200};
    font-size: 10px;
  }

  @media (max-width: 720px), (pointer: coarse) {
    top: 8px;
    left: 8px;
    display: inline-flex;
    padding: 2px 5px;
    font-size: 6px;

    .anticon {
      font-size: 7px;
    }
  }
`;

const NextButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$ready',
})<{ $ready?: boolean }>`
  border-color: ${({ theme }) => theme.COLORS.ACCENT._200}88;
  color: ${({ theme }) => theme.COLORS.ACCENT._200};
  background: ${({ theme }) => theme.COLORS.ACCENT._800}18;

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.COLORS.ACCENT._200} !important;
    color: ${({ theme }) => theme.COLORS.ACCENT._100} !important;
    background: ${({ theme }) => theme.COLORS.ACCENT._800}28 !important;
  }

  &.ant-btn[disabled],
  &.ant-btn[disabled]:hover {
    border-color: ${({ theme }) => theme.COLORS.ACCENT._800}66 !important;
    color: ${({ theme }) => theme.COLORS.ACCENT._200}66 !important;
    background: ${({ theme }) => theme.COLORS.BLACK._400} !important;
  }

  ${({ $ready }) =>
    $ready
      ? `
        background: #39d5c9;
        border-color: #39d5c9;
        box-shadow: 0 0 22px rgb(57 213 201 / 0.34);
        color: #ffffff;

        &:hover,
        &:focus {
          background: #2abfb4 !important;
          border-color: #65fff3 !important;
          color: #ffffff !important;
        }
      `
      : ''}

  @media (max-width: 720px), (pointer: coarse) {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    width: 78px;
    height: 56px;
    line-height: 1;
    white-space: normal;

    .anticon {
      margin: 0 !important;
      font-size: 18px;
    }
  }
`;

const ToolbarCenter = styled('div')`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;

  @media (max-width: 720px), (pointer: coarse) {
    gap: 4px;
  }
`;

const MaterialTitle = styled(PageTitle)`
  min-width: 0;
  margin: 0;
  text-align: center;

  @media (max-width: 720px) {
    display: -webkit-box;
    overflow: hidden;
    font-size: 16px;
    line-height: 1.22;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const getPlaylistItemDisplayName = (item: TPlaylistContentItem) =>
  getAiManagerModule1DisplayTitle(item.s3Key, item.name);

const isRunningInsideFrame = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const isMobileLandscapeViewport = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse) and (orientation: landscape)').matches;

const markPlaylistItemComplete = (
  playlist: TPlayListItem,
  completedIndex: number
): TPlayListItem => ({
  ...playlist,
  items: playlist.items.map((playlistItem, index) => {
    if (index === completedIndex) {
      return { ...playlistItem, completed: true };
    }

    if (index === completedIndex + 1) {
      return { ...playlistItem, locked: false };
    }

    return playlistItem;
  }),
});

const findMaterialGroupPath = (
  nodes: TCourseTreeNode[],
  itemIndex: number,
  path: TCourseTreeNode[] = []
): TCourseTreeNode[] | null => {
  for (const node of nodes) {
    if (node.type === 'material' && node.itemRef?.index === itemIndex) {
      return path;
    }

    const result = findMaterialGroupPath(node.children, itemIndex, [
      ...path,
      node,
    ]);
    if (result) return result;
  }

  return null;
};

const getNestedFilePath = (
  playlistId: string,
  groupPath: TCourseTreeNode[] | null,
  itemIndex: number
): string => {
  const [moduleNode, topicNode, lessonNode] = groupPath ?? [];
  const subtopicNode = groupPath?.[3];

  if (moduleNode && topicNode && lessonNode && subtopicNode) {
    return getPlayCourseSubtopicFilePath(
      playlistId,
      moduleNode.key,
      topicNode.key,
      lessonNode.key,
      subtopicNode.key,
      itemIndex
    );
  }

  if (moduleNode && topicNode && lessonNode) {
    return getPlayCourseFilePath(
      playlistId,
      moduleNode.key,
      topicNode.key,
      lessonNode.key,
      itemIndex
    );
  }

  return getPlayCourseItemPath(playlistId, itemIndex);
};

export const PlayCourseItemPage: FC = () => {
  const navigate = useNavigate();
  const params = useParams<{
    [PLAYLIST_ID_PARAM_NAME]: string;
    itemIndex: string;
    lessonKey?: string;
    moduleKey?: string;
    subtopicKey?: string;
    topicKey?: string;
  }>();
  const playlistId = params[PLAYLIST_ID_PARAM_NAME];
  const itemIndex = Number(params.itemIndex);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [item, setItem] = useState<TPlaylistContentItem>();
  const [playlist, setPlaylist] = useState<TPlayListItem>();
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [playerIndexes, setPlayerIndexes] = useState<TPlayerIndexes | null>(
    null
  );
  const isFramed = isRunningInsideFrame();
  const optimisticCompletedIndexesRef = useRef(new Set<number>());

  const applyOptimisticCompletions = useCallback((data: TPlayListItem) => {
    let nextData = data;
    optimisticCompletedIndexesRef.current.forEach((completedIndex) => {
      nextData = markPlaylistItemComplete(nextData, completedIndex);
    });

    return nextData;
  }, []);

  const resolveProgressItem = useCallback(
    (data: TPlayListItem) => {
      const nextPlaylist = applyOptimisticCompletions(data);
      const progressItem = nextPlaylist.items[itemIndex];

      return {
        nextPlaylist,
        progressItem,
      };
    },
    [applyOptimisticCompletions, itemIndex]
  );

  const loadProgress = useCallback(async () => {
    if (!playlistId || !Number.isInteger(itemIndex)) return;
    const response = await API.playlists.getById(playlistId);
    const { nextPlaylist, progressItem } = resolveProgressItem(response.data);
    setPlaylist(nextPlaylist);
    if (progressItem) {
      setItem(progressItem);
      if (!progressItem.locked) {
        setDenied(false);
      }
    }
  }, [itemIndex, playlistId, resolveProgressItem]);

  useEffect(() => {
    let active = true;

    if (!playlistId || !Number.isInteger(itemIndex)) {
      setDenied(true);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setDenied(false);
    setLoading(true);
    setItem(undefined);
    setPlayerIndexes(null);

    const load = async () => {
      const [itemResult, playlistResult] = await Promise.allSettled([
        API.playlists.getItem(playlistId, itemIndex),
        API.playlists.getById(playlistId),
      ]);

      if (!active) return;

      const directItem =
        itemResult.status === 'fulfilled' ? itemResult.value.data : undefined;
      const progressData =
        playlistResult.status === 'fulfilled'
          ? resolveProgressItem(playlistResult.value.data)
          : undefined;
      const progressItem = progressData?.progressItem;

      if (progressData) {
        setPlaylist(progressData.nextPlaylist);
      }

      const resolvedItem =
        progressItem && !progressItem.locked ? progressItem : directItem;

      if (!resolvedItem || resolvedItem.locked || progressItem?.locked) {
        setDenied(true);
        return;
      }

      setItem(resolvedItem);
      setDenied(false);
    };

    void load()
      .catch(() => {
        if (active) {
          setDenied(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [itemIndex, playlistId, resolveProgressItem]);

  useEffect(() => {
    const timer = window.setInterval(() => void loadProgress(), 5000);
    return () => window.clearInterval(timer);
  }, [loadProgress]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) {
        return;
      }

      if (event.data?.type === 'plai:qualified-complete') {
        optimisticCompletedIndexesRef.current.add(itemIndex);
        setItem((current) =>
          current ? { ...current, completed: true } : current
        );
        setPlaylist((current) =>
          current ? markPlaylistItemComplete(current, itemIndex) : current
        );
        void loadProgress();
        window.setTimeout(() => void loadProgress(), 1500);
      }

      if (event.data?.type === 'plai:indexes-update') {
        const indexes = event.data.indexes ?? {};
        setPlayerIndexes({
          amazement: normalizeIndexValue(indexes.amazement),
          attention: normalizeIndexValue(indexes.attention),
          happiness: normalizeIndexValue(indexes.happiness),
          involvement: normalizeIndexValue(indexes.involvement),
          tiredness: normalizeIndexValue(indexes.tiredness),
        });
      }

      if (
        event.data?.type === 'plai:playback-debug' &&
        isDebugVideoPlayerEnabled()
      ) {
        console.info('[EmbeddedPlaybackDebug]', {
          ...event.data,
          itemIndex,
          itemId: item?.id,
          itemName: item?.name,
        });
      }

      if (
        event.data?.type === 'plai:qualification-debug' &&
        isDebugVideoPlayerEnabled()
      ) {
        console.info('[EmbeddedQualificationDebug]', {
          ...event.data,
          itemIndex,
          itemId: item?.id,
          itemName: item?.name,
          itemCompleted: item?.completed,
        });
      }

      if (
        event.data?.type === 'plai:watch-result-debug' &&
        isDebugVideoPlayerEnabled()
      ) {
        console.info('[EmbeddedWatchResultDebug]', {
          ...event.data,
          itemIndex,
          itemId: item?.id,
          itemName: item?.name,
          itemCompleted: item?.completed,
        });
      }

      if (
        event.data?.type === 'plai:socket-debug' &&
        isDebugVideoPlayerEnabled()
      ) {
        console.info('[EmbeddedSocketDebug]', {
          ...event.data,
          itemIndex,
          itemId: item?.id,
          itemName: item?.name,
          itemCompleted: item?.completed,
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [item?.id, item?.name, itemIndex, loadProgress]);

  const frameUrl = useMemo(() => {
    if (!item) return '';
    if (isVideoPlaylistItem(item)) {
      const playerUrl = new URL(getPlayerLink(item.linkId));
      playerUrl.searchParams.set('courseEmbed', '1');
      if (isDebugVideoPlayerEnabled()) {
        playerUrl.searchParams.set('debugVideoPlayer', 'true');
      }
      return playerUrl.toString();
    }

    const fromPath =
      playlistId &&
      params.moduleKey &&
      params.topicKey &&
      params.lessonKey &&
      params.subtopicKey
        ? getPlayCourseSubtopicPath(
            playlistId,
            params.moduleKey,
            params.topicKey,
            params.lessonKey,
            params.subtopicKey
          )
        : playlistId && params.moduleKey && params.topicKey && params.lessonKey
          ? getPlayCourseLessonPath(
              playlistId,
              params.moduleKey,
              params.topicKey,
              params.lessonKey
            )
          : getPlayCoursePath(playlistId ?? '');

    const frameParams = new URLSearchParams({
      assetLinkId: item.linkId,
      embedded: '1',
      from: fromPath,
    });

    if (item.s3Key) frameParams.set('key', item.s3Key);
    if (item.kind) frameParams.set('kind', item.kind);
    if (item.name) frameParams.set('name', item.name);
    if (item.size) frameParams.set('size', item.size);
    if (item.completed) frameParams.set('qualified', '1');

    return `${Paths.MODULE1_VIEW}?${frameParams.toString()}`;
  }, [
    item,
    params.lessonKey,
    params.moduleKey,
    params.subtopicKey,
    params.topicKey,
    playlistId,
  ]);

  useEffect(() => {
    setPlayerIndexes(null);
  }, [frameUrl]);
  const isCurrentItemVideo = item ? isVideoPlaylistItem(item) : false;
  useEffect(() => {
    if (isCurrentItemVideo && frameUrl) {
      if (isDebugVideoPlayerEnabled()) {
        console.info('[EmbeddedPlaybackDebug] frame-url-changed', {
          frameUrl,
          itemIndex,
          itemId: item?.id,
          itemName: item?.name,
        });
      }
    }
  }, [frameUrl, isCurrentItemVideo, item?.id, item?.name, itemIndex]);
  const scrollFrameIntoView = useCallback(() => {
    if (!isMobileLandscapeViewport()) return;

    window.requestAnimationFrame(() => {
      frameRef.current?.scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: 'auto',
      });
    });
  }, []);
  const courseTree = useMemo(
    () =>
      playlist ? buildCourseTree(playlist.items, playlist.courseStructure) : [],
    [playlist]
  );
  const currentGroupPath = useMemo(
    () => findMaterialGroupPath(courseTree, itemIndex),
    [courseTree, itemIndex]
  );
  const backPath = useMemo(() => {
    const [moduleNode, topicNode, lessonNode] = currentGroupPath ?? [];
    const subtopicNode = currentGroupPath?.[3];

    if (playlistId && moduleNode && topicNode && lessonNode && subtopicNode) {
      return getPlayCourseSubtopicPath(
        playlistId,
        moduleNode.key,
        topicNode.key,
        lessonNode.key,
        subtopicNode.key
      );
    }

    if (playlistId && moduleNode && topicNode && lessonNode) {
      return getPlayCourseLessonPath(
        playlistId,
        moduleNode.key,
        topicNode.key,
        lessonNode.key
      );
    }

    return getPlayCoursePath(playlistId ?? '');
  }, [currentGroupPath, playlistId]);

  const postStudentAuthToPlayer = useCallback(() => {
    if (!isCurrentItemVideo || !frameUrl) return;

    const token = localStorage.getItem(TOKEN_LS_PATH);
    if (!token) return;

    if (isDebugVideoPlayerEnabled()) {
      console.info('[EmbeddedPlaybackDebug] post-student-auth', {
        itemIndex,
        itemId: item?.id,
        itemName: item?.name,
        targetOrigin: new URL(frameUrl).origin,
      });
    }

    frameRef.current?.contentWindow?.postMessage(
      {
        type: 'plai:student-auth',
        token,
        debugVideoPlayer: isDebugVideoPlayerEnabled(),
      },
      new URL(frameUrl).origin
    );
  }, [frameUrl, isCurrentItemVideo, item?.id, item?.name, itemIndex]);

  const handleFrameLoad = useCallback(() => {
    if (isCurrentItemVideo && isDebugVideoPlayerEnabled()) {
      console.info('[EmbeddedPlaybackDebug] iframe-load', {
        frameUrl,
        itemIndex,
        itemId: item?.id,
        itemName: item?.name,
      });
    }
    postStudentAuthToPlayer();
    scrollFrameIntoView();
  }, [
    frameUrl,
    isCurrentItemVideo,
    item?.id,
    item?.name,
    itemIndex,
    postStudentAuthToPlayer,
    scrollFrameIntoView,
  ]);

  useEffect(() => {
    if (!isCurrentItemVideo || !frameUrl) return;

    postStudentAuthToPlayer();
    const interval = window.setInterval(postStudentAuthToPlayer, 2000);

    return () => window.clearInterval(interval);
  }, [frameUrl, isCurrentItemVideo, postStudentAuthToPlayer]);

  useEffect(() => {
    if (!frameUrl) return;

    const timeout = window.setTimeout(scrollFrameIntoView, 120);
    window.addEventListener('resize', scrollFrameIntoView);
    window.addEventListener('orientationchange', scrollFrameIntoView);

    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('resize', scrollFrameIntoView);
      window.removeEventListener('orientationchange', scrollFrameIntoView);
    };
  }, [frameUrl, itemIndex, scrollFrameIntoView]);

  if (isFramed) {
    return (
      <Result
        status="403"
        title="Nested course view is blocked"
        subTitle="Course materials must open only in the top-level player."
      />
    );
  }

  if (loading) return <Spin />;
  if (denied || !item || !playlistId) {
    return (
      <Result
        status="403"
        title="This material is locked"
        subTitle="Complete the previous course material first."
        extra={
          <Button onClick={() => navigate(Paths.PLAYCOURSE)}>
            Back to courses
          </Button>
        }
      />
    );
  }

  const nextItem = playlist?.items[itemIndex + 1];
  const canOpenNext = Boolean(item.completed && nextItem && !nextItem.locked);
  const itemDisplayName = getPlaylistItemDisplayName(item);

  return (
    <PageFrame>
      {playlist && (
        <CourseCarousel
          courseStructure={playlist.courseStructure}
          currentItemIndex={itemIndex}
          items={playlist.items}
          mode="fileNavigator"
          playlistId={playlistId}
        />
      )}
      <Toolbar>
        <CourseButton
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(backPath)}
        >
          Course
        </CourseButton>
        <ToolbarCenter>
          {!isCurrentItemVideo && (
            <MaterialTitle>
              {item.order ?? itemIndex + 1}. {itemDisplayName}
            </MaterialTitle>
          )}
          {isCurrentItemVideo && (
            <VideoPlayerIndexesBar
              indexes={playerIndexes}
              title={`${item.order ?? itemIndex + 1}. ${itemDisplayName}`}
            />
          )}
        </ToolbarCenter>
        <NextButton
          type="primary"
          icon={<ArrowRightOutlined />}
          disabled={!canOpenNext}
          $ready={canOpenNext}
          onClick={() => {
            const nextGroupPath = findMaterialGroupPath(
              courseTree,
              itemIndex + 1
            );
            navigate(
              getNestedFilePath(playlistId, nextGroupPath, itemIndex + 1)
            );
          }}
        >
          Next
        </NextButton>
      </Toolbar>
      <ViewerFrame $qualified={Boolean(item.completed)}>
        {item.completed && isCurrentItemVideo && (
          <QualifiedBadge>
            <CheckCircleOutlined />
            Qualified study completed
          </QualifiedBadge>
        )}
        <Viewer
          key={item?.id ?? itemIndex}
          ref={frameRef}
          $fileViewer={!isCurrentItemVideo}
          title={itemDisplayName}
          src={frameUrl}
          allow="camera; microphone; autoplay; fullscreen"
          onLoad={handleFrameLoad}
        />
      </ViewerFrame>
    </PageFrame>
  );
};
